/**
 * Deposit percentage options for the checkout picker: 10–100% in 10-point
 * steps.
 *
 * The 10% floor is `MIN_DEPOSIT_RATIO`
 * (`shared/depositGate.ts`), which every server gate
 * has clamped an explicit `depositPercent` to since 2026-06-17 (Lesson #46).
 * The picker was the last surface still narrower than the rule behind it — a
 * rep could not offer a deposit the platform had always been willing to
 * accept. Pinned by
 * `functions/src/transitions/__tests__/depositOptionsParity.test.ts`, which
 * compares these bounds against the SHARED server helper rather than a
 * literal.
 *
 * `snapDepositPercent` maps any loaded value (e.g. a non-multiple of 10) onto
 * the nearest valid option, so editing an old quote shows a real dropdown
 * value instead of a blank select.
 *
 * WIDENING CONSEQUENCE, deliberate: a legacy quote stored below 50% used to
 * snap UP on load — a 20% quote displayed 50 and re-saved as 50, silently
 * raising the rep's own money term. It now displays and re-saves 20. The
 * server has always gated that quote at 20% via `resolveDepositRatio`, so this
 * makes the UI agree with the gate; it does not change the gate.
 */
export const DEPOSIT_OPTIONS = [10, 20, 30, 40, 50, 60, 70, 80, 90, 100] as const;

/** Derived from the option list, never hand-written: `snapDepositPercent` must
 *  never produce a value the dropdown cannot render (a `<select>` whose `value`
 *  matches no `<option>` renders blank). */
const FLOOR: number = DEPOSIT_OPTIONS[0];
const CEILING: number = DEPOSIT_OPTIONS[DEPOSIT_OPTIONS.length - 1];

export function snapDepositPercent(pct: number): number {
  const roundedToTen = Math.round(pct / 10) * 10;
  return Math.min(CEILING, Math.max(FLOOR, roundedToTen));
}
